'use client';

import { useQueryStates } from 'nuqs';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { BlogPageType, urlMap } from '@/fo/feature/article/page/blog';
import {
  ArticleBanner,
  UiArticleBannerBlog
} from '@/fo/ui/article/banner/blog';
import { UiArticleHeaderBlog } from '@/fo/ui/article/header/blog';
import {
  ArticleItem,
  DEFAULT_SORT_OPTION,
  SortOptionEnum,
  UiArticleListBase
} from '@/fo/ui/article/list/base';
import { UiKitLabelBaseProps } from '@/fo/ui/kit/label/base';

import { getPosts } from '../../api/getPosts';
import { topicSearchParams } from '../../helpers/searchParams';
import { FilterType } from '../../types';

export interface PageTopicClientProps {
  postsList: ArticleItem[];
  hasNextPage: boolean;
  bannerList: ArticleBanner[];
  alias: string;
  tag?: UiKitLabelBaseProps;
  filterType: FilterType;
  type: BlogPageType;
}

export function ClientComponent({
  postsList,
  hasNextPage: initialHasNextPage,
  bannerList,
  alias,
  tag,
  filterType,
  type
}: PageTopicClientProps) {
  const [posts, setPosts] = useState<ArticleItem[]>(postsList);
  const [urlParams, setUrlParams] = useQueryStates(topicSearchParams);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPosts = async (sort: SortOptionEnum, query?: string) => {
    const { posts: newPosts, hasNextPage: newHasNextPage } = await getPosts({
      type,
      alias,
      filterType,
      sort,
      currentPage,
      query
    });

    setCurrentPage(1);
    setPosts(newPosts);
    setHasNextPage(newHasNextPage);
  };

  const handleSortChange = async (value: SortOptionEnum) => {
    setUrlParams({
      sort: value === DEFAULT_SORT_OPTION ? null : value
    });

    await fetchPosts(value, urlParams.query);
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    const { posts: newPosts, hasNextPage: newHasNextPage } = await getPosts({
      type,
      alias,
      filterType,
      sort: urlParams.sort as SortOptionEnum,
      currentPage: currentPage + 1,
      query: urlParams.query
    });

    setPosts([...posts, ...newPosts]);
    setHasNextPage(newHasNextPage);
    setCurrentPage(currentPage + 1);
    setLoadingMore(false);
  };

  const handleSearch = useDebouncedCallback(async (value: string) => {
    setUrlParams({
      query: value
    });

    await fetchPosts(urlParams.sort, value);
  }, 400);

  return (
    <div>
      <UiArticleHeaderBlog />
      <UiArticleBannerBlog banners={bannerList} />
      <UiArticleListBase
        onSortChange={handleSortChange}
        onLoadMore={handleLoadMore}
        showMore={hasNextPage}
        loadingMore={loadingMore}
        sort={urlParams.sort as SortOptionEnum}
        items={posts}
        initialQuery={urlParams.query}
        tag={tag}
        requestCancelUrl={`/about-skin/${urlMap[type]}/`}
        onSearch={handleSearch}
      />
    </div>
  );
}
